import { useState, useRef, useEffect } from "react";
import { useLayoutConfig } from "../contexts/layout-context.js";
import { createLayoutService } from "../services/layout-service.js";
const useLayout = (layoutType, params = {}) => {
  const config = useLayoutConfig();
  const [areas, setAreas] = useState([]);
  const [layout, setLayout] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isLoadingRef = useRef(false);
  const paramsStringRef = useRef("");
  const fetchLayout = async () => {
    if (isLoadingRef.current) {
      return;
    }
    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);
    try {
      const layoutService = createLayoutService(config);
      const response = await layoutService.fetchLayout(layoutType, params);
      if (response.success && response.data && response.data.template) {
        setAreas(response.data.template.areas || []);
        setLayout(response.data.template.layout);
      } else {
        setError(response.error || "Failed to load layout");
        setAreas([]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
      setError(errorMessage);
      setAreas([]);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  };
  useEffect(() => {
    const paramsString = JSON.stringify(params);
    if (paramsString !== paramsStringRef.current) {
      paramsStringRef.current = paramsString;
      fetchLayout();
    } else if (paramsStringRef.current === "") {
      paramsStringRef.current = paramsString;
      fetchLayout();
    }
  }, [layoutType, JSON.stringify(params)]);
  return {
    areas,
    layout,
    isLoading,
    error,
    refetch: fetchLayout
  };
};
const useMultipleLayouts = (layoutTypes) => {
  const config = useLayoutConfig();
  const [results, setResults] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  useEffect(() => {
    const fetchLayouts = async () => {
      const layoutService = createLayoutService(config);
      const responses = await layoutService.fetchMultipleLayouts(layoutTypes);
      const newResults = {};
      Object.entries(responses).forEach(([layoutType, response]) => {
        newResults[layoutType] = {
          areas: response.success && response.data && response.data.template ? response.data.template.areas : [],
          layout: response.success && response.data && response.data.template ? response.data.template.layout : null,
          isLoading: false,
          error: response.success ? null : response.error || "Failed to load layout",
          refetch: async () => {
            const newResponse = await layoutService.fetchLayout(layoutType);
            if (newResponse.success && newResponse.data) {
              setResults((prev) => ({
                ...prev,
                [layoutType]: {
                  ...prev[layoutType],
                  areas: newResponse.data.template.areas,
                  layout: newResponse.data.template.layout,
                  error: null
                }
              }));
            }
          }
        };
      });
      setResults(newResults);
      setIsInitialized(true);
    };
    fetchLayouts();
  }, [layoutTypes.join(",")]);
  if (!isInitialized) {
    return layoutTypes.reduce((acc, layoutType) => {
      acc[layoutType] = {
        areas: [],
        layout: null,
        isLoading: true,
        error: null,
        refetch: async () => {
        }
      };
      return acc;
    }, {});
  }
  return results;
};
export {
  useLayout,
  useMultipleLayouts
};
//# sourceMappingURL=use-layout.js.map
